import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FlaskConical, ArrowRight, TriangleAlert, ShieldCheck, Sparkles } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { listFunds } from "@/lib/api/funds";
import {
  gatherFundMfnContext,
  simulateGrant,
  type HypotheticalGrant,
} from "@/lib/mfn/engine-context";
import { type StructuredValue } from "@argus/engine";
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Dot,
  EmptyState,
  Spinner,
} from "@/components/ui/primitives";
import { fmtUsd } from "@/lib/format";

type GrantKind =
  | "mgmt_fee_discount"
  | "carry_modification"
  | "expense_cap"
  | "co_invest"
  | "transparency";

const GRANT_KINDS: {
  value: GrantKind;
  label: string;
  unit: "pct" | "usd" | "none";
  placeholder?: string;
}[] = [
  {
    value: "mgmt_fee_discount",
    label: "Management fee discount",
    unit: "pct",
    placeholder: "1.50",
  },
  {
    value: "carry_modification",
    label: "Reduced carried interest",
    unit: "pct",
    placeholder: "15",
  },
  { value: "expense_cap", label: "Fund expense cap", unit: "usd", placeholder: "2000000" },
  { value: "co_invest", label: "Co-investment rights", unit: "none" },
  { value: "transparency", label: "Enhanced transparency / information rights", unit: "none" },
];

function buildGrant(lpId: string, kind: GrantKind, value: string): HypotheticalGrant {
  const num = Number(value);
  const structuredValue: StructuredValue = {};
  if (kind === "mgmt_fee_discount") structuredValue.fee_pct = num;
  if (kind === "carry_modification") structuredValue.carry_pct = num;
  if (kind === "expense_cap") structuredValue.cap_usd = num;
  return { lpId, category: kind, structuredValue };
}

function SimulatorInner() {
  const tenant = useTenant();
  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });

  const [fundId, setFundId] = useState<string>("");
  const activeFundId = fundId !== "" ? fundId : (funds?.[0]?.id ?? "");

  const { data: ctx, isLoading: ctxLoading } = useQuery({
    queryKey: ["mfn-context", tenant.id, activeFundId],
    queryFn: () => gatherFundMfnContext(tenant.id, activeFundId),
    enabled: !!activeFundId,
  });

  const [lpId, setLpId] = useState<string>("");
  const [kind, setKind] = useState<GrantKind>("mgmt_fee_discount");
  const [value, setValue] = useState<string>("1.50");
  const [submitted, setSubmitted] = useState<HypotheticalGrant | null>(null);

  const kindDef = GRANT_KINDS.find((g) => g.value === kind);
  const unit = kindDef?.unit ?? "none";

  // LPs that have a commitment in this fund — the meaningful grantees.
  const lpOptions = useMemo(() => {
    if (!ctx) return [];
    return Object.keys(ctx.commitments)
      .map((id) => ({
        id,
        name: ctx.lpsById.get(id)?.short_name ?? ctx.lpsById.get(id)?.legal_name ?? "LP",
        commitment: ctx.commitments[id] ?? 0,
      }))
      .sort((a, b) => b.commitment - a.commitment);
  }, [ctx]);

  const activeLpId = lpId !== "" ? lpId : (lpOptions[0]?.id ?? "");

  // On first load of a fund, auto-select the most consequential scenario so the
  // simulator opens on a result that demonstrates the cascade rather than a
  // blank slate. (Granting a strong fee discount to whichever LP triggers the
  // most other holders.)
  const autoForFund = useRef<string>("");
  useEffect(() => {
    if (!ctx || lpOptions.length === 0 || autoForFund.current === activeFundId) return;
    autoForFund.current = activeFundId;
    let best: { lpId: string; count: number; exposed: number } | null = null;
    for (const lp of lpOptions) {
      const sim = simulateGrant(ctx, buildGrant(lp.id, "mgmt_fee_discount", "1.00"));
      const count = sim.newlyEntitled.length;
      if (
        count > 0 &&
        (!best ||
          count > best.count ||
          (count === best.count && sim.exposedCommitment > best.exposed))
      ) {
        best = { lpId: lp.id, count, exposed: sim.exposedCommitment };
      }
    }
    if (best) {
      setLpId(best.lpId);
      setKind("mgmt_fee_discount");
      setValue("1.00");
      setSubmitted(buildGrant(best.lpId, "mgmt_fee_discount", "1.00"));
    }
  }, [ctx, lpOptions, activeFundId]);

  const result = useMemo(() => {
    if (!ctx || !submitted) return null;
    return simulateGrant(ctx, submitted);
  }, [ctx, submitted]);

  const run = () => {
    if (!activeLpId) return;
    setSubmitted(buildGrant(activeLpId, kind, value));
  };

  const granteeName =
    lpOptions.find((l) => l.id === (submitted?.lpId ?? activeLpId))?.name ?? "this LP";

  return (
    <>
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-neutral-50">
          <FlaskConical className="text-brand-400 size-5" /> What-if simulator
        </h1>
        <p className="max-w-2xl text-sm text-neutral-400">
          Model a term before you grant it. Argus replays every LP&apos;s MFN clause against the
          hypothetical and tells you exactly who becomes entitled to elect it — and what commitment
          that exposes — <span className="text-neutral-300">before</span> it&apos;s in a signed side
          letter.
        </p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[360px_1fr]">
        {/* Grant builder */}
        <Card className="h-fit">
          <CardHeader title="Hypothetical grant" />
          <div className="space-y-4 p-4 pt-1">
            <Field label="Fund">
              <select
                className="input"
                value={activeFundId}
                onChange={(e) => {
                  setFundId(e.target.value);
                  setSubmitted(null);
                }}
              >
                {funds?.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Grant this LP">
              <select
                className="input"
                value={activeLpId}
                onChange={(e) => setLpId(e.target.value)}
              >
                {lpOptions.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} — {fmtUsd(l.commitment)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Term">
              <select
                className="input"
                value={kind}
                onChange={(e) => setKind(e.target.value as GrantKind)}
              >
                {GRANT_KINDS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
            </Field>

            {unit !== "none" && (
              <Field label={unit === "pct" ? "Rate (%)" : "Cap (USD)"}>
                <div className="relative">
                  {unit === "usd" && (
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500">
                      $
                    </span>
                  )}
                  <input
                    type="number"
                    step={unit === "pct" ? "0.05" : "100000"}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className={`input ${unit === "usd" ? "pl-6" : ""} ${unit === "pct" ? "pr-7" : ""}`}
                    placeholder={kindDef?.placeholder}
                  />
                  {unit === "pct" && (
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500">
                      %
                    </span>
                  )}
                </div>
              </Field>
            )}

            <Button
              variant="primary"
              className="w-full"
              onClick={run}
              disabled={!ctx || ctxLoading}
            >
              <Sparkles className="size-4" /> Run simulation
            </Button>
            <p className="text-[11px] leading-relaxed text-neutral-500">
              Runs entirely on the deterministic MFN engine — no document is created and nothing is
              persisted.{" "}
              {ctx
                ? `${ctx.clauses.length} MFN clause${ctx.clauses.length === 1 ? "" : "s"} in scope.`
                : ""}
            </p>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-5">
          {ctxLoading ? (
            <Card className="flex items-center gap-2 p-10 text-sm text-neutral-500">
              <Spinner /> Loading fund context…
            </Card>
          ) : !result ? (
            <EmptyState
              icon={<FlaskConical className="size-8" />}
              title="Build a scenario to see the cascade"
              description="Pick an LP and a term, then run the simulation. Argus shows which other LPs could elect the same benefit under their most-favored-nation rights."
            />
          ) : (
            <>
              <Card
                className={
                  result.newlyEntitled.length > 0
                    ? "border-amber-800/50 bg-amber-950/10"
                    : "border-emerald-800/40 bg-emerald-950/10"
                }
              >
                <div className="flex items-start gap-4 p-5">
                  <div
                    className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${result.newlyEntitled.length > 0 ? "bg-amber-950/60 text-amber-300" : "bg-emerald-950/60 text-emerald-300"}`}
                  >
                    {result.newlyEntitled.length > 0 ? (
                      <TriangleAlert className="size-5" />
                    ) : (
                      <ShieldCheck className="size-5" />
                    )}
                  </div>
                  <div className="space-y-1">
                    {result.newlyEntitled.length > 0 ? (
                      <>
                        <p className="text-lg font-semibold text-neutral-50">
                          {result.newlyEntitled.length} LP
                          {result.newlyEntitled.length === 1 ? "" : "s"} could elect this term
                        </p>
                        <p className="text-sm text-neutral-300">
                          Granting <span className="font-medium">{granteeName}</span> this benefit
                          would expose{" "}
                          <span className="font-semibold tabular-nums text-amber-300">
                            {fmtUsd(result.exposedCommitment)}
                          </span>{" "}
                          of commitments to a most-favored-nation election.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-lg font-semibold text-neutral-50">No MFN cascade</p>
                        <p className="text-sm text-neutral-300">
                          Granting <span className="font-medium">{granteeName}</span> this term does
                          not trigger any other LP&apos;s most-favored-nation rights.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </Card>

              {result.newlyEntitled.length > 0 && (
                <Card>
                  <CardHeader title="LPs newly entitled to elect" />
                  <ul className="divide-y divide-neutral-800/70 px-2 pb-2">
                    {result.newlyEntitled.map((t) => (
                      <li
                        key={t.holderLpId}
                        className="flex items-start justify-between gap-3 px-2 py-3"
                      >
                        <div className="min-w-0 space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-neutral-100">{t.holderName}</span>
                            <Badge tone="neutral">
                              {fmtUsd(t.holderCommitment ?? 0)} committed
                            </Badge>
                          </div>
                          <p className="text-xs leading-relaxed text-neutral-400">{t.rationale}</p>
                        </div>
                        <Badge tone={t.severity === "breach" ? "breach" : "risk"}>
                          <Dot tone={t.severity === "breach" ? "breach" : "risk"} />
                          {Math.round(t.confidence * 100)}%
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              <div className="flex items-center justify-between rounded-lg border border-neutral-800/70 bg-neutral-900/40 px-4 py-3 text-xs text-neutral-500">
                <span className="flex items-center gap-1.5">
                  Baseline conflicts on book:{" "}
                  <span className="text-neutral-300">{result.baseline.length}</span>
                </span>
                <ArrowRight className="size-3.5" />
                <span className="flex items-center gap-1.5">
                  With this grant:{" "}
                  <span className="text-neutral-300">{result.allTriggers.length}</span>
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
        {label}
      </span>
      {children}
    </label>
  );
}

export function SimulatorPage() {
  return (
    <TenantLayout>
      <SimulatorInner />
    </TenantLayout>
  );
}
